import { useEffect, useMemo } from 'react';
import { useQuery, skipToken } from '@apollo/client/react';

import {
    PortfolioReportDocument,
    IPortfolioReportOrderField,
    ISortDirection,
    IPortfolioReportQueryVariables,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { usePagination } from 'common/hooks/usePagination';
import { TReportType } from 'report/types';
import { useReportFilter, useReportQueryFilterValues } from 'report/hooks';
import { useSortBy } from 'common/hooks/useSortBy';

export const useReportList = (
    type: TReportType,
    skip?: boolean,
    extraVariables?: Partial<IPortfolioReportQueryVariables>
) => {
    const { showApolloError } = useApolloError();
    const { filters, queryFilterValues, setQueryFilterValues } =
        useReportQueryFilterValues(type);

    const { sortBy, onSort } = useSortBy<IPortfolioReportOrderField>(
        IPortfolioReportOrderField.StartDate,
        ISortDirection.Desc
    );

    const {
        filterValues,
        filterValuesForQuery,
        onFilterChangeValue,
        handleSearch: onSearch,
        handleFilterChange: onFilterChange,
    } = useReportFilter(filters, type);

    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    // Stringify extraVariables so identity changes (callers passing inline
    // object literals) don't invalidate the memo on every render.
    const extraVariablesKey = JSON.stringify(extraVariables);

    const variables = useMemo(
        () => ({
            ...paginationVariables,
            ...filterValuesForQuery,
            ...extraVariables,
            sortBy,
        }),
        [paginationVariables, filterValuesForQuery, extraVariablesKey, sortBy]
    );

    const { loading, data, refetch, error } = useQuery(
        PortfolioReportDocument,
        skip
            ? skipToken
            : {
                  variables,
                  fetchPolicy: 'network-only',
                  notifyOnNetworkStatusChange: true,
              }
    );

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const portfolioReport = data?.portfolioReport;
    const items = useMemo(
        () =>
            portfolioReport?.edges
                .map((edge) => edge?.node || null)
                .filter(Boolean) || [],
        [portfolioReport?.edges]
    );

    const count = portfolioReport?.count || 0;
    const reportQueryFilterValues = portfolioReport?.filterValues || undefined;

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(count || 0);
    }, [count, loading, initializePagination]);

    // Update query filter values whenever there is a change in the reportQueryFilterValues
    useEffect(() => {
        if (
            (!reportQueryFilterValues && !queryFilterValues) ||
            !reportQueryFilterValues ||
            JSON.stringify(reportQueryFilterValues) ===
                JSON.stringify(queryFilterValues)
        ) {
            return;
        }

        setQueryFilterValues(reportQueryFilterValues);
    }, [reportQueryFilterValues, queryFilterValues, setQueryFilterValues]);

    return {
        items,
        count,
        loading,
        onSearch,
        onFilterChange,
        onFilterChangeValue,
        filters,
        filterValues,
        onSort,
        currentSort: sortBy,
        setPage,
        paginationSettings,
        filterValuesForQuery,
        sortBy,
        refetch,
    };
};
